import * as Popover from '@radix-ui/react-popover';
import classNames from 'classnames';
import React, { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTransferCustomization } from '@wetransfer/transfer/component/TransferCustomizationProvider/hooks';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import { useEffectOnce } from '@wetransfer/transfer/hooks/useEffectOnce';
import { trackAmplitudeEvent } from '@wetransfer/transfer/modules/analytics/vendors/amplitude';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import I18n from '@wetransfer/transfer/modules/i18n';
import { Pill } from '@wetransfer/transfer/oneui/react';
import Actions from '@wetransfer/transfer/store/action';
import { selectUiIsOptionsVisible } from '@wetransfer/transfer/store/selector/ui';

import { CaretUpIcon } from './CaretUpIcon/CaretUpIcon';
import styles from './CustomWallpaperPopover.module.scss';
import {
  CustomWallpaperPopoverProps,
  CustomWallpapersFeaturePromptEvent,
} from './types';
import { preventDefault } from './utils';

export const CustomWallpaperPopover: FC<CustomWallpaperPopoverProps> = ({
  target,
  onViewPopover,
  onDismissPopover,
  onAcceptPopover,
}) => {
  const { openDrawer, isDrawerOpen } = useTransferCustomization();

  // Report popover view once on mount
  useEffectOnce(() => {
    onViewPopover();

    const event: CustomWallpapersFeaturePromptEvent = {
      action: 'Feature Prompt',
      additionalProperties: {
        Action: 'Shown',
        Feature: 'Custom Wallpaper',
        Component: 'Popover',
        Context: 'Transfer Window',
      },
    };
    trackAmplitudeEvent(event);
  });

  const dispatch = useDispatch();
  const isOptionsOpen = useSelector(selectUiIsOptionsVisible);

  const openOptionsAndDrawer = useCallback(() => {
    if (isDrawerOpen) {
      onAcceptPopover();

      return;
    }

    if (isOptionsOpen) {
      onAcceptPopover();
      // Delay by a cycle to avoid being overridden by useClickOutside handler
      setTimeout(openDrawer, 0);

      return;
    }
    onAcceptPopover();
    dispatch(Actions.UI.setUploaderOptions({ visible: true }));
    // Open drawer after options scroll animation
    setTimeout(openDrawer, 250);
  }, [onAcceptPopover, dispatch, isDrawerOpen, isOptionsOpen, openDrawer]);

  const isDismissActionPermanent =
    Number(
      Cookie.get(AppConfig.cookies.customWallpaperPopoverSeenCount.name) || 0
    ) > 1;

  const handleConfirm = useCallback(() => {
    const event: CustomWallpapersFeaturePromptEvent = {
      action: 'Feature Prompt',
      additionalProperties: {
        Action: 'Confirm',
        Feature: 'Custom Wallpaper',
        Component: 'Popover',
        Context: 'Transfer Window',
      },
    };
    trackAmplitudeEvent(event);

    openOptionsAndDrawer();
  }, [openOptionsAndDrawer]);

  const trackDismiss = useCallback(() => {
    const event: CustomWallpapersFeaturePromptEvent = {
      action: 'Feature Prompt',
      additionalProperties: {
        Action: 'Dismiss',
        Feature: 'Custom Wallpaper',
        Component: 'Popover',
        Context: 'Transfer Window',
      },
    };
    trackAmplitudeEvent(event);
  }, []);

  const handleDismiss = useCallback(() => {
    trackDismiss();
    onDismissPopover();
  }, [trackDismiss, onDismissPopover]);

  const handleDismissForever = useCallback(() => {
    trackDismiss();
    onDismissPopover(true);
  }, [trackDismiss, onDismissPopover]);

  const handleClickOutside = useCallback(() => {
    const event: CustomWallpapersFeaturePromptEvent = {
      action: 'Feature Prompt',
      additionalProperties: {
        Action: 'Click Outside',
        Feature: 'Custom Wallpaper',
        Component: 'Popover',
        Context: 'Transfer Window',
      },
    };
    trackAmplitudeEvent(event);

    onDismissPopover();
  }, [onDismissPopover]);

  return (
    <Popover.Root open>
      <Popover.Anchor asChild>{target}</Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          className={styles.content}
          sideOffset={7}
          align="start"
          alignOffset={-38}
          onOpenAutoFocus={preventDefault}
          onInteractOutside={handleClickOutside}
        >
          <Pill
            textContent={I18n.t('custom-wallpaper.pop_over.new_feature')}
            variant="highlight"
            shape="round"
            size="small"
            className={styles.pill}
          />
          <p className={styles.description}>
            {I18n.t('custom-wallpaper.pop_over.introductory_upsell')}
          </p>
          <div className={styles.buttonRow}>
            <button
              onClick={handleConfirm}
              className={classNames(styles.button, styles.confirmButton)}
            >
              {I18n.t('custom-wallpaper.pop_over.try_now')}
            </button>
            <button
              onClick={
                isDismissActionPermanent ? handleDismissForever : handleDismiss
              }
              className={classNames(styles.button, styles.dismissButton)}
            >
              {isDismissActionPermanent
                ? I18n.t('custom-wallpaper.pop_over.hide_forever')
                : I18n.t('custom-wallpaper.pop_over.not_today')}
            </button>
          </div>
          <Popover.Arrow asChild width={28} height={9}>
            <CaretUpIcon className={styles.arrowIcon} />
          </Popover.Arrow>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
